import { useSyncExternalStore } from "react";
import { userStorageKey } from "./userStorageKey";

const subscribe = (callback: () => void) => {
  window.addEventListener("storage", callback);
  return () => {
    window.removeEventListener("storage", callback);
  };
};

/**
 * Persists a value for the signed-in employee. Everything an employee can set
 * belongs to them, not to the computer: they might share computers and OS
 * accounts, so an unscoped key can hand the next person whatever the previous
 * one left — which for a filter or a toggle means a partial view that reads as
 * a complete one, and for anything else means being confronted with someone
 * else's choices.
 *
 * `key` is scoped automatically — pass the bare name, not a prefixed one.
 *
 * `userId` is `undefined` only above the auth guard, where sign-in has not
 * happened yet and there is nobody to store a preference for. Nothing is read
 * or written then, and `initialValue` stands in.
 */
export const useLocalStorage = ({
  key,
  userId,
  initialValue = "",
}: {
  key: string;
  userId: string | undefined;
  initialValue?: string;
}) => {
  const scopedKey =
    userId === undefined ? undefined : userStorageKey({ userId, key });

  const storedValue = useSyncExternalStore(subscribe, () =>
    scopedKey === undefined
      ? initialValue
      : (window.localStorage.getItem(scopedKey) ?? initialValue),
  );

  const setValue = (value: string) => {
    if (scopedKey === undefined) {
      return;
    }
    window.localStorage.setItem(scopedKey, value);
    window.dispatchEvent(new Event("storage"));
  };

  return [storedValue, setValue] as const;
};
